/**
 * SmartStudio.jsx — Prashasta Smart Event Studio
 * Premium adaptive event configurator.
 *
 * Route:  /smart-studio  (NEW — does not replace /build-my-event until UX-05)
 * Phases: Discover → Personalise → Configure → Review → Connect
 *
 * Design: Prashasta official identity — ivory canvas, champagne gold, charcoal,
 *         Cormorant Garamond headings, DM Sans body. Minimal motion.
 *
 * Question Engine: deterministic versioned conditional rules (no AI in flow).
 * Pricing:         server-side GQP only; debounced; stale responses ignored.
 * CRM:             publicLeadApi.createProvisionalLead on Connect submit.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from "@/contexts/AuthContext";
import {
  Heart, Gem, Gift, Briefcase, MonitorPlay, Zap, Award, Sparkles,
  MapPin, Camera, Mic2, Music, Users, Car, Mail, UtensilsCrossed,
  ChevronRight, ChevronDown, ChevronUp, Check, X,
  Loader2, Phone, User, ArrowRight, RotateCcw,
  Building2, Star, MessageCircle, RefreshCw, Info, LogIn, LayoutDashboard,
} from 'lucide-react';
import { PJ } from '../journey/journeyConfig';
import { publicLeadApi, publicEstimateApi } from '../services/api';
import {
  PHASES, PHASE_IDS, TENANT_ALIAS, PLANNING_SCOPES,
  EVENT_TYPES, SERVICES, CITIES, GUEST_RANGES, VENUE_STATUSES,
  STYLE_OPTIONS, BUDGET_RANGES,
  createEmptyStudioState, getVisibleQuestions, isPhaseComplete,
  getInvalidatedIds, buildEstimateRequest, resolveGuestCount,
} from './smart-studio/StudioConfig';
import EnquiryConfirmationScreen from '../components/EnquiryConfirmationScreen';

// ── CSS ───────────────────────────────────────────────────────────────────────
const SS_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --ss-ivory:   #FBF7F2;
    --ss-white:   #FFFFFF;
    --ss-obsidian:#201B19;
    --ss-gold:    #C9A15F;
    --ss-gold-lt: #E8D8B6;
    --ss-blush:   #F5E7E8;
    --ss-sand:    #EEE4DA;
    --ss-forest:  #35654D;
    --ss-slate:   #746D69;
    --ss-border:  rgba(32,27,25,0.09);
    --ss-shadow:  0 8px 32px -8px rgba(32,27,25,0.10);
    --ss-shadow-lg: 0 20px 48px -12px rgba(32,27,25,0.14);
  }

  .ss-fade-in   { animation: ssFadeIn 0.36s cubic-bezier(0.22,1,0.36,1) both; }
  .ss-slide-up  { animation: ssSlideUp 0.42s cubic-bezier(0.22,1,0.36,1) both; }
  .ss-stagger-1 { animation-delay: 0.06s; }
  .ss-stagger-2 { animation-delay: 0.12s; }
  .ss-stagger-3 { animation-delay: 0.18s; }
  .ss-stagger-4 { animation-delay: 0.24s; }
  .ss-stagger-5 { animation-delay: 0.30s; }

  @keyframes ssFadeIn  { from{opacity:0} to{opacity:1} }
  @keyframes ssSlideUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }

  .ss-event-card:not([data-sel=true]):hover { transform:translateY(-3px); box-shadow:0 12px 32px -8px rgba(32,27,25,0.16)!important; }
  .ss-event-card { transition:transform 0.22s cubic-bezier(0.22,1,0.36,1), box-shadow 0.22s; }
  .ss-chip:hover  { background:var(--ss-sand)!important; }
  .ss-svc-card:hover { box-shadow:0 6px 24px -6px rgba(201,161,95,0.22)!important; }
  .ss-btn-gold:hover { background:var(--ss-obsidian)!important; }
  .ss-btn-outline:hover { background:var(--ss-sand)!important; }

  .ss-drawer-overlay { position:fixed;inset:0;background:rgba(32,27,25,0.38);z-index:49;backdrop-filter:blur(2px); }
  .ss-drawer { position:fixed;bottom:0;left:0;right:0;z-index:50;background:#fff;border-radius:20px 20px 0 0;
    padding:20px 20px 32px;max-height:72vh;overflow-y:auto;
    box-shadow:0 -8px 40px rgba(32,27,25,0.18); animation:ssSlideUp 0.34s cubic-bezier(0.22,1,0.36,1) both; }

  .ss-sticky-summary { position:sticky;top:88px; }
  .ss-progress-dot { transition:all 0.28s cubic-bezier(0.22,1,0.36,1); }

  input[type="date"]::-webkit-calendar-picker-indicator { opacity:0.4;cursor:pointer; }
  input[type="number"]::-webkit-inner-spin-button { opacity:0.5; }

  @media (max-width:768px) {
    .ss-sidebar { display:none!important; }
    .ss-mobile-bar { display:flex!important; }
    .ss-layout { grid-template-columns:1fr!important; }
  }
  @media (min-width:769px) {
    .ss-mobile-bar { display:none!important; }
  }
`;

// ── Icon maps ─────────────────────────────────────────────────────────────────
const EVENT_ICONS = {
  WEDDING: Heart, ENGAGEMENT: Gem, BIRTHDAY: Gift, CORPORATE: Briefcase,
  CONFERENCE: MonitorPlay, PRODUCT_LAUNCH: Zap, AWARDS: Award, OTHER: Sparkles,
};
const SERVICE_ICONS = {
  VENUE: MapPin, DECOR: Sparkles, CATERING: UtensilsCrossed, PHOTOGRAPHY: Camera,
  AV_PRODUCTION: Mic2, ENTERTAINMENT: Music, GUEST_HOSPITALITY: Users,
  TRANSPORT: Car, INVITATIONS: Mail,
};

// ── Formatters ────────────────────────────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

// ── Consent / Session constants ───────────────────────────────────────────────
const CONSENT_WORDING = "I agree that Prashasta Events may contact me regarding my event enquiry by phone, WhatsApp or email, using the contact details I have provided.";
const CONSENT_VERSION = "CONTACT_CONSENT_V1_0";
const SESSION_KEY    = 'prashasta-studio-session-v1';
const SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24 h

// ── Category → Service Code map (canonical DB IDs → SmartStudio values) ──────
const CATEGORY_SERVICE_MAP = {
  'ueim-sc-venue-001':       'VENUE',
  'ueim-sc-catering-001':    'CATERING',
  'ueim-sc-decor-001':       'DECOR',
  'ueim-sc-photo-001':       'PHOTOGRAPHY',
  'ueim-sc-entertain-001':   'ENTERTAINMENT',
  'ueim-sc-hospitality-001': 'GUEST_HOSPITALITY',
  'ueim-sc-av-001':          'AV_PRODUCTION',
  'ueim-sc-transport-001':   'TRANSPORT',
  'ueim-sc-invite-001':      'INVITATIONS',
};

// ── Keyboard chip handler (Enter/Space activates chip-style buttons) ──────────
const handleChipKey = (e, action) => {
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); action(); }
};

// ══════════════════════════════════════════════════════════════════════════════
// SHARED SUB-COMPONENTS
// ══════════════════════════════════════════════════════════════════════════════

function QuestionLabel({ label, subLabel }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.55rem', fontWeight: 600,
        color: PJ.obsidian, margin: 0, lineHeight: 1.25 }}>{label}</h3>
      {subLabel && (
        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', color: PJ.slate,
          margin: '6px 0 0', lineHeight: 1.5 }}>{subLabel}</p>
      )}
    </div>
  );
}

function PhaseSection({ title, children, delay = 0 }) {
  return (
    <div className="ss-slide-up" style={{ animationDelay: `${delay}s`, marginBottom: 36 }}>
      {title && (
        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', fontWeight: 600,
          color: PJ.gold, textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 20px' }}>
          {title}
        </p>
      )}
      {children}
    </div>
  );
}

function GoldDivider() {
  return <div style={{ height: 1, background: `linear-gradient(to right, ${PJ.gold}40, transparent)`, margin: '32px 0' }} />;
}

// ══════════════════════════════════════════════════════════════════════════════
// PHASE 1: DISCOVER
// ══════════════════════════════════════════════════════════════════════════════

// ── UX-PUBLIC-01: Compact event-type confirmation banner ─────────────────────
function EventTypeConfirmedBanner({ eventType, onChangeEventType }) {
  const et = EVENT_TYPES.find(e => e.value === eventType);
  if (!et) return null;
  const Icon = EVENT_ICONS[et.value] || Sparkles;
  return (
    <div
      data-testid="ss-event-type-confirmed-banner"
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 18px', background: PJ.blush, borderRadius: 14,
        border: `1.5px solid ${PJ.gold}40`, marginBottom: 24,
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 12,
        background: `${PJ.gold}22`, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={18} style={{ color: PJ.gold }} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{
          margin: 0, fontFamily: 'DM Sans, sans-serif', fontSize: '0.68rem',
          fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: PJ.gold,
        }}>
          Event Type
        </p>
        <p style={{
          margin: 0, fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem',
          fontWeight: 600, color: PJ.obsidian, lineHeight: 1.2,
        }}>
          {et.label}
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <div style={{
          width: 22, height: 22, borderRadius: '50%', background: PJ.gold,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Check size={11} style={{ color: '#fff' }} />
        </div>
        <button
          data-testid="ss-change-event-type-btn"
          onClick={onChangeEventType}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem', color: PJ.slate,
            textDecoration: 'underline', padding: '2px 6px',
          }}
        >
          Change
        </button>
      </div>
    </div>
  );
}

function PhaseDiscover({ answers, onChange, eventTypeFromUrl = false, onChangeEventType }) {
  return (
    <div>
      {/* Event Type — UX-PUBLIC-01: show compact banner when pre-confirmed from Discover screen */}
      {eventTypeFromUrl && answers.event_type ? (
        <PhaseSection title="Your Event">
          <EventTypeConfirmedBanner
            eventType={answers.event_type}
            onChangeEventType={onChangeEventType}
          />
        </PhaseSection>
      ) : (
        <PhaseSection title="Step 1 of 2 — Your Event">
          <QuestionLabel label="What are you celebrating?" subLabel="Your event type shapes the entire planning experience" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 14 }}>
            {EVENT_TYPES.map((et, i) => {
              const Icon = EVENT_ICONS[et.value] || Sparkles;
              const sel = answers.event_type === et.value;
              return (
                <button
                  key={et.value}
                  data-testid={`ss-event-type-${et.value.toLowerCase()}`}
                  data-sel={sel}
                  className="ss-event-card"
                  aria-pressed={sel}
                  aria-label={et.label}
                  onClick={() => onChange('event_type', et.value)}
                  onKeyDown={e => handleChipKey(e, () => onChange('event_type', et.value))}
                  style={{
                    position: 'relative',
                    background: sel ? PJ.blush : PJ.white,
                    border: sel ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
                    borderRadius: 18, padding: '18px 14px', cursor: 'pointer',
                    textAlign: 'center', boxShadow: sel ? `0 6px 24px -6px ${PJ.gold}40` : 'none',
                    transition: 'all 0.22s',
                  }}
                >
                  <div style={{ width: 44, height: 44, borderRadius: 14, margin: '0 auto 10px',
                    background: sel ? `${PJ.gold}22` : `${PJ.sand}80`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={20} style={{ color: sel ? PJ.gold : PJ.slate }} />
                  </div>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem',
                    color: PJ.obsidian, margin: 0, lineHeight: 1.3 }}>{et.label}</p>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', color: PJ.slate,
                    margin: '4px 0 0', lineHeight: 1.3 }}>{et.description}</p>
                  {sel && (
                    <div style={{ position: 'absolute', top: 10, right: 10, width: 20, height: 20, borderRadius: '50%',
                      background: PJ.gold, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Check size={11} style={{ color: '#fff' }} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </PhaseSection>
      )}

      {answers.event_type && (
        <>
          <GoldDivider />
          <PhaseSection title={eventTypeFromUrl && answers.event_type ? "Now, the basics" : "Step 2 of 2 — The Basics"} delay={0.04}>
            {/* City */}
            <div style={{ marginBottom: 28 }}>
              <QuestionLabel label="Where will this take place?" />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 10 }}>
                {CITIES.map(c => {
                  const sel = answers.location_city?.value === c.value;
                  return (
                    <button key={c.value} data-testid={`ss-city-${c.value.toLowerCase()}`}
                      className="ss-chip"
                      aria-pressed={sel}
                      onClick={() => onChange('location_city', c)}
                      onKeyDown={e => handleChipKey(e, () => onChange('location_city', c))}
                      style={{ background: sel ? PJ.blush : PJ.white,
                        border: sel ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
                        borderRadius: 12, padding: '11px 14px', cursor: 'pointer',
                        fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', fontWeight: sel ? 600 : 400,
                        color: sel ? PJ.obsidian : PJ.slate, transition: 'all 0.18s',
                        display: 'flex', alignItems: 'center', gap: 8 }}>
                      <MapPin size={14} style={{ color: sel ? PJ.gold : PJ.slate, flexShrink: 0 }} />
                      {c.label}
                    </button>
                  );
                })}
              </div>
              {answers.location_city?.value === 'OTHER' && (
                <input
                  data-testid="ss-city-custom-input"
                  placeholder="Enter your city"
                  value={answers.location_custom}
                  onChange={e => onChange('location_custom', e.target.value)}
                  style={{ marginTop: 10, width: '100%', maxWidth: 280, padding: '10px 14px',
                    border: `1.5px solid ${PJ.sand}`, borderRadius: 10,
                    fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', color: PJ.obsidian,
                    background: PJ.white, outline: 'none', boxSizing: 'border-box' }}
                />
              )}
            </div>

            {/* Date */}
            <div style={{ marginBottom: 28 }}>
              <QuestionLabel label="When is the event?" subLabel="Optional — helps with availability and seasonal pricing" />
              <input
                type="date"
                data-testid="ss-event-date-input"
                value={answers.event_date}
                min={new Date().toISOString().split('T')[0]}
                onChange={e => onChange('event_date', e.target.value)}
                style={{ padding: '10px 14px', border: `1.5px solid ${PJ.sand}`, borderRadius: 10,
                  fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', color: PJ.obsidian,
                  background: PJ.white, outline: 'none', minWidth: 200 }}
              />
            </div>

            {/* Guest Count */}
            <div>
              <QuestionLabel label="How many guests?" subLabel="Used for service-level pricing" />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(120px,1fr))', gap: 10, marginBottom: 14 }}>
                {GUEST_RANGES.map(gr => {
                  const sel = answers.guest_count_range?.value === gr.value;
                  return (
                    <button key={gr.value} data-testid={`ss-guests-${gr.value.toLowerCase()}`}
                      className="ss-chip"
                      aria-pressed={sel}
                      onClick={() => onChange('guest_count_range', gr)}
                      onKeyDown={e => handleChipKey(e, () => onChange('guest_count_range', gr))}
                      style={{ background: sel ? PJ.blush : PJ.white,
                        border: sel ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
                        borderRadius: 12, padding: '10px 12px', cursor: 'pointer',
                        fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', fontWeight: sel ? 600 : 400,
                        color: sel ? PJ.obsidian : PJ.slate, transition: 'all 0.18s', textAlign: 'center' }}>
                      {gr.label}
                    </button>
                  );
                })}
              </div>
              {answers.guest_count_range && answers.guest_count_range.value !== 'NOT_SURE' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', color: PJ.slate }}>
                    Exact count (optional):
                  </span>
                  <input
                    type="number"
                    data-testid="ss-guest-count-exact-input"
                    placeholder="e.g. 450"
                    min={1} max={10000}
                    value={answers.guest_count_exact}
                    onChange={e => onChange('guest_count_exact', e.target.value)}
                    style={{ width: 110, padding: '8px 12px', border: `1.5px solid ${PJ.sand}`,
                      borderRadius: 10, fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem',
                      color: PJ.obsidian, background: PJ.white, outline: 'none' }}
                  />
                </div>
              )}
            </div>
          </PhaseSection>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PHASE 2: PERSONALISE
// ══════════════════════════════════════════════════════════════════════════════

function PhasePersonalise({ answers, onChange }) {
  const visibleQs = getVisibleQuestions('PERSONALIZE', answers);
  if (visibleQs.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px' }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: `${PJ.gold}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <Check size={24} style={{ color: PJ.gold }} />
        </div>
        <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', color: PJ.obsidian, margin: 0 }}>
          Your event details look good!
        </p>
        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', color: PJ.slate, marginTop: 8 }}>
          No additional personalisation questions for this event type. Continue to configure your services.
        </p>
      </div>
    );
  }
  return (
    <div>
      {/* Wedding functions */}
      {visibleQs.find(q => q.id === 'wedding_functions') && (
        <PhaseSection title="Your Celebrations" delay={0}>
          <QuestionLabel label="Which celebrations are you planning?" subLabel="Each can be styled and planned independently" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {[
              {value:'ENGAGEMENT',label:'Engagement'},{value:'HALDI',label:'Haldi'},
              {value:'MEHENDI',label:'Mehendi'},{value:'SANGEETH',label:'Sangeet'},
              {value:'WEDDING',label:'Wedding'},{value:'RECEPTION',label:'Reception'},
              {value:'OTHER',label:'Other'},
            ].map(fn => {
              const sel = (answers.wedding_functions || []).includes(fn.value);
              return (
                <button key={fn.value} data-testid={`ss-wfn-${fn.value.toLowerCase()}`}
                  className="ss-chip"
                  aria-pressed={sel}
                  onClick={() => {
                    const cur = answers.wedding_functions || [];
                    onChange('wedding_functions', sel ? cur.filter(v => v !== fn.value) : [...cur, fn.value]);
                  }}
                  onKeyDown={e => handleChipKey(e, () => {
                    const cur = answers.wedding_functions || [];
                    onChange('wedding_functions', sel ? cur.filter(v => v !== fn.value) : [...cur, fn.value]);
                  })}
                  style={{ background: sel ? PJ.blush : PJ.white,
                    border: sel ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
                    borderRadius: 24, padding: '9px 18px', cursor: 'pointer',
                    fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', fontWeight: sel ? 600 : 400,
                    color: sel ? PJ.obsidian : PJ.slate, transition: 'all 0.18s',
                    display: 'flex', alignItems: 'center', gap: 6 }}>
                  {sel && <Check size={13} style={{ color: PJ.gold }} />}
                  {fn.label}
                </button>
              );
            })}
          </div>
        </PhaseSection>
      )}

      {/* Conference type */}
      {visibleQs.find(q => q.id === 'conference_type') && (
        <>
          {answers.wedding_functions?.length > 0 && <GoldDivider />}
          <PhaseSection title="Event Format" delay={0.04}>
            <QuestionLabel label="What type of event is this?" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 12 }}>
              {[
                {value:'CORPORATE_CONF',label:'Corporate Conference',desc:'Internal or external'},
                {value:'PARTNER_SUMMIT',label:'Partner Summit',desc:'Multi-stakeholder summits'},
                {value:'LEADERSHIP_MEET',label:'Leadership Meet',desc:'Executive gatherings'},
                {value:'EXHIBITION',label:'Exhibition / Expo',desc:'Large-format exhibitions'},
                {value:'CONF_AWARDS',label:'Conference + Awards',desc:'Combined format'},
                {value:'NOT_SURE',label:'Not Sure Yet',desc:'Help me figure it out'},
              ].map(ct => {
                const sel = answers.conference_type === ct.value;
                return (
                  <button key={ct.value} data-testid={`ss-conf-type-${ct.value.toLowerCase()}`}
                    className="ss-chip"
                    aria-pressed={sel}
                    onClick={() => onChange('conference_type', ct.value)}
                    onKeyDown={e => handleChipKey(e, () => onChange('conference_type', ct.value))}
                    style={{ background: sel ? PJ.blush : PJ.white,
                      border: sel ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
                      borderRadius: 14, padding: '14px 16px', cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.18s' }}>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.88rem',
                      color: PJ.obsidian, margin: 0 }}>{ct.label}</p>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.76rem', color: PJ.slate, margin: '3px 0 0' }}>
                      {ct.desc}
                    </p>
                  </button>
                );
              })}
            </div>
          </PhaseSection>
        </>
      )}

      {/* Style preference */}
      {visibleQs.find(q => q.id === 'style_preference') && (
        <>
          <GoldDivider />
          <PhaseSection title="Your Aesthetic" delay={0.08}>
            <QuestionLabel label="What style resonates with you?" subLabel="Guides curated recommendations and decor approach" />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {STYLE_OPTIONS.map(s => {
                const sel = answers.style_preference === s.value;
                return (
                  <button key={s.value} data-testid={`ss-style-${s.value.toLowerCase()}`}
                    className="ss-chip"
                    aria-pressed={sel}
                    onClick={() => onChange('style_preference', sel ? null : s.value)}
                    onKeyDown={e => handleChipKey(e, () => onChange('style_preference', sel ? null : s.value))}
                    style={{ background: sel ? PJ.blush : PJ.white,
                      border: sel ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
                      borderRadius: 24, padding: '9px 20px', cursor: 'pointer',
                      fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', fontWeight: sel ? 600 : 400,
                      color: sel ? PJ.obsidian : PJ.slate, transition: 'all 0.18s',
                      display: 'flex', alignItems: 'center', gap: 6 }}>
                    {sel && <Check size={13} style={{ color: PJ.gold }} />}
                    {s.label}
                  </button>
                );
              })}
            </div>
          </PhaseSection>
        </>
      )}

      {/* Budget */}
      <GoldDivider />
      <PhaseSection title="Your Budget" delay={0.12}>
        <QuestionLabel label="What is your approximate budget?" subLabel="Helps tailor our recommendations. You can refine this later." />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 10 }}>
          {BUDGET_RANGES.map(b => {
            const sel = answers.budget_range === b.value;
            return (
              <button key={b.value} data-testid={`ss-budget-${b.value.toLowerCase()}`}
                className="ss-chip"
                aria-pressed={sel}
                onClick={() => onChange('budget_range', sel ? null : b.value)}
                onKeyDown={e => handleChipKey(e, () => onChange('budget_range', sel ? null : b.value))}
                style={{ background: sel ? PJ.blush : PJ.white,
                  border: sel ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
                  borderRadius: 12, padding: '11px 14px', cursor: 'pointer', textAlign: 'center',
                  fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', fontWeight: sel ? 600 : 400,
                  color: sel ? PJ.obsidian : PJ.slate, transition: 'all 0.18s' }}>
                {b.label}
              </button>
            );
          })}
        </div>
      </PhaseSection>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// FEATURED PARTNERS (UX-HOME-01)
// ══════════════════════════════════════════════════════════════════════════════

const EMPTY_STATE_MSG = "Our specialists will help you discover the perfect partners for your celebration.";

// Reverse map: service_code → category_id
const SERVICE_CATEGORY_MAP = {
  'VENUE':             'ueim-sc-venue-001',
  'CATERING':          'ueim-sc-catering-001',
  'DECOR':             'ueim-sc-decor-001',
  'PHOTOGRAPHY':       'ueim-sc-photo-001',
  'ENTERTAINMENT':     'ueim-sc-entertain-001',
  'GUEST_HOSPITALITY': 'ueim-sc-hospitality-001',
  'AV_PRODUCTION':     'ueim-sc-av-001',
  'TRANSPORT':         'ueim-sc-transport-001',
  'INVITATIONS':       'ueim-sc-invite-001',
};

function PartnerCard({ partner, isPreferred, onToggle }) {
  const initial = (partner.name || '?')[0].toUpperCase();
  return (
    <div data-testid={`partner-card-${partner.id}`}
      style={{ background: isPreferred ? PJ.blush : PJ.white,
        border: isPreferred ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
        borderRadius: 16, padding: '16px 18px', transition: 'all 0.22s',
        boxShadow: isPreferred ? `0 4px 18px -4px ${PJ.gold}30` : 'none',
        minWidth: 200, maxWidth: 240, flexShrink: 0 }}>
      {/* Avatar */}
      <div style={{ width: 44, height: 44, borderRadius: 12, marginBottom: 10,
        background: isPreferred ? `${PJ.gold}22` : PJ.sand,
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.3rem', fontWeight: 600,
          color: isPreferred ? PJ.gold : PJ.slate }}>{initial}</span>
      </div>
      <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.9rem',
        color: PJ.obsidian, margin: '0 0 4px', lineHeight: 1.3 }}>{partner.name}</p>
      {partner.city && (
        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.76rem', color: PJ.slate,
          margin: '0 0 2px', display: 'flex', alignItems: 'center', gap: 4 }}>
          <MapPin size={11} style={{ color: PJ.gold, flexShrink: 0 }} />
          {partner.city}
        </p>
      )}
      {partner.tagline && (
        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.74rem', color: PJ.slate,
          margin: '4px 0 0', lineHeight: 1.4, fontStyle: 'italic' }}>
          "{partner.tagline}"
        </p>
      )}
      <button data-testid={`partner-prefer-${partner.id}`}
        onClick={() => onToggle(partner)}
        style={{ marginTop: 12, width: '100%', padding: '8px 12px',
          background: isPreferred ? PJ.gold : 'transparent',
          border: `1.5px solid ${isPreferred ? PJ.gold : PJ.sand}`,
          borderRadius: 10, cursor: 'pointer',
          fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem', fontWeight: 600,
          color: isPreferred ? '#fff' : PJ.slate, transition: 'all 0.18s',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        {isPreferred ? <><Check size={13} /> Preferred</> : <>+ Add to My Plan</>}
      </button>
    </div>
  );
}

function FeaturedPartnersForService({ serviceCode, preferredPartners, onTogglePartner, tenantSlug }) {
  const [partners, setPartners] = useState(null);
  const [loading, setLoading] = useState(true);
  const categoryId = SERVICE_CATEGORY_MAP[serviceCode];

  useEffect(() => {
    if (!categoryId || !tenantSlug) { setLoading(false); return; }
    setLoading(true);
    // B2-FIX: tenant_slug required in path — no hardcoded fallback
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/public/homepage/${encodeURIComponent(tenantSlug)}/categories/${encodeURIComponent(categoryId)}/featured-partners`
    )
      .then(r => r.ok ? r.json() : null)
      .then(data => { setPartners(data); setLoading(false); })
      .catch(() => { setPartners(null); setLoading(false); });
  }, [categoryId, tenantSlug]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
      <Loader2 size={14} style={{ color: PJ.gold, animation: 'spin 1s linear infinite' }} />
      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem', color: PJ.slate }}>Finding partners…</span>
    </div>
  );

  const list = partners?.partners || [];
  const emptyMsg = partners?.empty_state_message || EMPTY_STATE_MSG;
  const svcLabel = SERVICES.find(s => s.value === serviceCode)?.label || serviceCode;

  return (
    <div style={{ marginBottom: 20 }}>
      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', fontWeight: 600,
        color: PJ.gold, textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 12px' }}>
        Featured {svcLabel} Partners
      </p>
      {list.length === 0 ? (
        <div style={{ background: `${PJ.gold}08`, border: `1px solid ${PJ.gold}20`,
          borderRadius: 12, padding: '14px 16px' }}>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem', color: PJ.slate,
            margin: 0, lineHeight: 1.5, fontStyle: 'italic' }}>{emptyMsg}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8,
          scrollbarWidth: 'thin' }}>
          {list.map(p => {
            const pref = preferredPartners?.[serviceCode];
            const isPreferred = pref?.supplier_id === p.id;
            return (
              <PartnerCard key={p.id} partner={p} isPreferred={isPreferred}
                onToggle={(partner) => onTogglePartner(serviceCode, partner)} />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PHASE 3: CONFIGURE
// ══════════════════════════════════════════════════════════════════════════════

function ServiceCard({ svc, selected, onToggle }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = SERVICE_ICONS[svc.value] || Sparkles;
  const INCLUSIONS = {
    VENUE: ['Venue selection & negotiation', 'Site visits', 'Contract review', 'Backup venue shortlist'],
    DECOR: ['Theme concept & mood board', 'Floral arrangements', 'Lighting design', 'Stage & entrance decor'],
    CATERING: ['Menu planning', 'Multi-cuisine buffet', 'Live counters', 'F&B staffing'],
    PHOTOGRAPHY: ['Pre-event & event coverage', 'Edited digital album', 'Same-day highlights'],
    AV_PRODUCTION: ['Stage & LED wall', 'PA system', 'Lighting rig', 'AV crew'],
    ENTERTAINMENT: ['Artist coordination', 'DJ & sound', 'Live performances'],
    GUEST_HOSPITALITY: ['Guest list management', 'Welcome kits', 'On-site concierge'],
    TRANSPORT: ['Fleet arrangement', 'Route planning', 'Guest movement coordination'],
    INVITATIONS: ['Digital & print design', 'RSVP management', 'Guest communication'],
  };
  return (
    <div data-testid={`ss-service-${svc.value.toLowerCase()}`}
      className="ss-svc-card"
      style={{ background: selected ? PJ.blush : PJ.white,
        border: selected ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
        borderRadius: 16, overflow: 'hidden', transition: 'all 0.22s',
        boxShadow: selected ? `0 4px 18px -4px ${PJ.gold}30` : 'none' }}>
      <div style={{ padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}
        onClick={onToggle}>
        <div style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: selected ? `${PJ.gold}22` : `${PJ.sand}80`,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={20} style={{ color: selected ? PJ.gold : PJ.slate }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.9rem',
            color: PJ.obsidian, margin: 0 }}>{svc.label}</p>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.76rem', color: PJ.slate, margin: '2px 0 0' }}>
            {svc.desc}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <div style={{ width: 22, height: 22, borderRadius: '50%',
            background: selected ? PJ.gold : 'transparent',
            border: selected ? 'none' : `1.5px solid ${PJ.sand}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {selected && <Check size={12} style={{ color: '#fff' }} />}
          </div>
        </div>
      </div>
      {INCLUSIONS[svc.value] && (
        <div>
          <button onClick={() => setExpanded(e => !e)}
            style={{ width: '100%', padding: '8px 18px', background: 'transparent',
              border: 'none', borderTop: `1px solid ${selected ? PJ.gold + '30' : PJ.sand}`,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.76rem', color: PJ.slate }}>
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {expanded ? 'Hide' : 'View'} inclusions
          </button>
          {expanded && (
            <div style={{ padding: '0 18px 14px' }}>
              <ul style={{ margin: 0, padding: '0 0 0 16px', listStyle: 'none' }}>
                {INCLUSIONS[svc.value].map(inc => (
                  <li key={inc} style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.79rem',
                    color: PJ.slate, marginBottom: 4, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <span style={{ color: PJ.gold, marginTop: 2, flexShrink: 0 }}>•</span>{inc}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PhaseConfigure({ answers, onChange, preferredPartners, onTogglePartner }) {
  const activeServices = answers.planning_scope === 'LUXURY'
    ? ['VENUE', 'DECOR', 'CATERING', 'AV_PRODUCTION', 'GUEST_HOSPITALITY', 'PHOTOGRAPHY']
    : (answers.selected_services || []);

  return (
    <div>
      {/* Preselected category notice */}
      {answers.preselected_category_id && answers.planning_scope !== 'BUILD_OWN' && (
        <div data-testid="ss-preselected-category-notice"
          style={{ background: `${PJ.gold}0C`, border: `1px solid ${PJ.gold}30`,
            borderRadius: 12, padding: '12px 16px', marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 10 }}>
          <Check size={15} style={{ color: PJ.gold, flexShrink: 0 }} />
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem', color: PJ.obsidian, margin: 0 }}>
            Choose <strong>Build My Own</strong> below to add the service we pre-selected for you, or go full Luxury Concierge.
          </p>
        </div>
      )}

      {/* Planning scope */}
      <PhaseSection title="Planning Approach">
        <QuestionLabel label="How would you like to plan?" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 16 }}>
          {PLANNING_SCOPES.map(scope => {
            const sel = answers.planning_scope === scope.value;
            return (
              <button key={scope.value} data-testid={`ss-scope-${scope.value.toLowerCase()}`}
                onClick={() => onChange('planning_scope', scope.value)}
                style={{ background: sel ? PJ.blush : PJ.white,
                  border: sel ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
                  borderRadius: 18, padding: '22px 20px', cursor: 'pointer', textAlign: 'left',
                  boxShadow: sel ? `0 8px 28px -8px ${PJ.gold}40` : 'none', transition: 'all 0.22s',
                  position: 'relative' }}>
                {scope.badge && (
                  <span style={{ position: 'absolute', top: 14, right: 14,
                    background: PJ.gold, color: '#fff', fontSize: '0.68rem', fontWeight: 600,
                    borderRadius: 20, padding: '3px 10px', fontFamily: 'DM Sans, sans-serif' }}>
                    {scope.badge}
                  </span>
                )}
                <div style={{ width: 44, height: 44, borderRadius: 12, marginBottom: 14,
                  background: sel ? `${PJ.gold}22` : `${PJ.sand}80`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {scope.value === 'LUXURY' ? <Star size={20} style={{ color: sel ? PJ.gold : PJ.slate }} />
                    : <Building2 size={20} style={{ color: sel ? PJ.gold : PJ.slate }} />}
                </div>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', fontWeight: 600,
                  color: PJ.obsidian, margin: '0 0 4px', lineHeight: 1.2 }}>{scope.headline}</p>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: PJ.slate,
                  margin: 0, lineHeight: 1.45 }}>{scope.desc}</p>
              </button>
            );
          })}
        </div>
      </PhaseSection>

      {/* Service selection (BUILD_OWN only) */}
      {answers.planning_scope === 'BUILD_OWN' && (
        <>
          <GoldDivider />
          <PhaseSection title="Your Services" delay={0.06}>
            <QuestionLabel label="Choose your services" subLabel="Select what you need — expand each for inclusions" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
              {SERVICES.map(svc => (
                <ServiceCard key={svc.value} svc={svc}
                  selected={(answers.selected_services || []).includes(svc.value)}
                  onToggle={() => {
                    const cur = answers.selected_services || [];
                    const has = cur.includes(svc.value);
                    onChange('selected_services', has ? cur.filter(v => v !== svc.value) : [...cur, svc.value]);
                  }}
                />
              ))}
            </div>
          </PhaseSection>
        </>
      )}

      {/* Luxury auto-selected confirmation */}
      {answers.planning_scope === 'LUXURY' && (
        <>
          <GoldDivider />
          <PhaseSection title="Included in Your Package" delay={0.06}>
            <div style={{ background: `${PJ.gold}0C`, border: `1px solid ${PJ.gold}30`,
              borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: PJ.slate, margin: 0 }}>
                Your Luxury Concierge package includes end-to-end management of the following services:
              </p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {SERVICES.filter(s => ['VENUE','DECOR','CATERING','AV_PRODUCTION','GUEST_HOSPITALITY','PHOTOGRAPHY'].includes(s.value)).map(svc => {
                const Icon = SERVICE_ICONS[svc.value] || Sparkles;
                return (
                  <div key={svc.value} style={{ display: 'flex', alignItems: 'center', gap: 8,
                    background: PJ.blush, border: `1.5px solid ${PJ.gold}30`, borderRadius: 24,
                    padding: '8px 14px' }}>
                    <Icon size={14} style={{ color: PJ.gold }} />
                    <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem',
                      fontWeight: 500, color: PJ.obsidian }}>{svc.label}</span>
                    <Check size={13} style={{ color: PJ.forest }} />
                  </div>
                );
              })}
            </div>
          </PhaseSection>
        </>
      )}

      {/* ── UX-HOME-01: Featured Partner Discovery ──────────────────────────── */}
      {activeServices.length > 0 && (
        <>
          <GoldDivider />
          <PhaseSection title="Discover Our Partners" delay={0.12}>
            <QuestionLabel
              label="Would you like to express a partner preference?"
              subLabel="These are featured specialists in your selected categories. Selecting one is a preference — not a booking."
            />
            <div data-testid="ss-featured-partners-section">
              {activeServices.map(svcCode => (
                <FeaturedPartnersForService key={svcCode} serviceCode={svcCode}
                  preferredPartners={preferredPartners}
                  onTogglePartner={onTogglePartner}
                  tenantSlug={user?.tenant_id} />
              ))}
            </div>
          </PhaseSection>
        </>
      )}

      {/* Venue status */}
      {(answers.planning_scope === 'LUXURY' || (answers.selected_services || []).includes('VENUE')) && (
        <>
          <GoldDivider />
          <PhaseSection title="Venue" delay={0.1}>
            <QuestionLabel label="Do you have a venue in mind?" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 10 }}>
              {VENUE_STATUSES.map(vs => {
                const sel = answers.venue_status === vs.value;
                return (
                  <button key={vs.value} data-testid={`ss-venue-${vs.value.toLowerCase()}`}
                    className="ss-chip"
                    onClick={() => onChange('venue_status', vs.value)}
                    style={{ background: sel ? PJ.blush : PJ.white,
                      border: sel ? `2px solid ${PJ.gold}` : `1.5px solid ${PJ.sand}`,
                      borderRadius: 14, padding: '13px 16px', cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.18s' }}>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.88rem',
                      color: PJ.obsidian, margin: 0 }}>{vs.label}</p>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.76rem', color: PJ.slate, margin: '3px 0 0' }}>
                      {vs.desc}
                    </p>
                  </button>
                );
              })}
            </div>
          </PhaseSection>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PHASE 4: REVIEW
// ══════════════════════════════════════════════════════════════════════════════

function PhaseReview({ answers, estimate, estimateLoading, estimateError, onRecalculate }) {
  const activeServices = answers.planning_scope === 'LUXURY'
    ? SERVICES.filter(s => ['VENUE','DECOR','CATERING','AV_PRODUCTION','GUEST_HOSPITALITY','PHOTOGRAPHY'].includes(s.value))
    : SERVICES.filter(s => (answers.selected_services || []).includes(s.value));

  return (
    <div>
      <PhaseSection title="Your Event Plan">
        <QuestionLabel label="Here's what we're planning for you" subLabel="Review your selections before we finalise the estimate" />

        {/* Plan summary card */}
        <div style={{ background: PJ.white, border: `1.5px solid ${PJ.sand}`, borderRadius: 18,
          padding: '20px 24px', marginBottom: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
            {[
              { label: 'Event Type', value: EVENT_TYPES.find(e => e.value === answers.event_type)?.label || '—' },
              { label: 'Location', value: answers.location_city?.value === 'OTHER' ? answers.location_custom : (CITIES.find(c => c.value === answers.location_city?.value)?.label || '—') },
              { label: 'Date', value: answers.event_date ? new Date(answers.event_date).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'}) : 'Not specified' },
              { label: 'Guests', value: answers.guest_count_exact ? `${parseInt(answers.guest_count_exact).toLocaleString()} guests` : (answers.guest_count_range?.label || '—') },
              { label: 'Planning Type', value: PLANNING_SCOPES.find(s => s.value === answers.planning_scope)?.label || '—' },
              ...(answers.style_preference ? [{ label: 'Style', value: STYLE_OPTIONS.find(s => s.value === answers.style_preference)?.label || '—' }] : []),
            ].map(({ label, value }) => (
              <div key={label}>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', fontWeight: 600,
                  color: PJ.gold, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 2px' }}>
                  {label}
                </p>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', color: PJ.obsidian, margin: 0 }}>
                  {value}
                </p>
              </div>
            ))}
          </div>
          {activeServices.length > 0 && (
            <div style={{ marginTop: 16, borderTop: `1px solid ${PJ.sand}`, paddingTop: 14 }}>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', fontWeight: 600,
                color: PJ.gold, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 8px' }}>
                Services Included
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {activeServices.map(s => {
                  const Icon = SERVICE_ICONS[s.value] || Sparkles;
                  return (
                    <div key={s.value} style={{ display: 'flex', alignItems: 'center', gap: 6,
                      background: PJ.sand, borderRadius: 20, padding: '5px 12px' }}>
                      <Icon size={12} style={{ color: PJ.slate }} />
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.79rem', color: PJ.obsidian }}>
                        {s.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* UX-HOME-01: Partner preferences */}
          {answers.preferred_partners && Object.keys(answers.preferred_partners).length > 0 && (
            <div data-testid="ss-review-preferred-partners"
              style={{ marginTop: 16, borderTop: `1px solid ${PJ.sand}`, paddingTop: 14 }}>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', fontWeight: 600,
                color: PJ.gold, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 8px' }}>
                Partner Preferences
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {Object.entries(answers.preferred_partners).map(([svcCode, pref]) => {
                  const svc = SERVICES.find(s => s.value === svcCode);
                  return (
                    <div key={svcCode} style={{ display: 'flex', alignItems: 'center', gap: 6,
                      background: `${PJ.gold}12`, border: `1px solid ${PJ.gold}40`,
                      borderRadius: 20, padding: '5px 12px' }}>
                      <Star size={11} style={{ color: PJ.gold, fill: PJ.gold }} />
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.79rem', color: PJ.obsidian }}>
                        {pref.partner_name} <span style={{ color: PJ.slate }}>({svc?.label || svcCode})</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </PhaseSection>

      {/* GQP Estimate */}
      <GoldDivider />
      <PhaseSection title="Your Estimate">
        <QuestionLabel label="Indicative pricing" subLabel="Server-calculated using current catalog rates. Final quote after consultation." />

        {/* Gate 2: Guest-count assumption disclosure */}
        {(answers.guest_count_range || answers.guest_count_exact) && (
          <div data-testid="ss-guest-assumption-banner"
            style={{ background: answers.guest_count_exact ? `${PJ.forest}0C` : `${PJ.gold}0C`,
              border: `1.5px solid ${answers.guest_count_exact ? `${PJ.forest}40` : `${PJ.gold}40`}`,
              borderRadius: 12, padding: '12px 16px', marginBottom: 20,
              display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <Info size={15} style={{ color: answers.guest_count_exact ? PJ.forest : PJ.gold,
              flexShrink: 0, marginTop: 2 }} />
            <div>
              {answers.guest_count_exact ? (
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: PJ.obsidian, margin: 0 }}>
                  Priced for <strong>{parseInt(answers.guest_count_exact).toLocaleString()} guests</strong> (confirmed count).
                </p>
              ) : (
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: PJ.obsidian, margin: 0 }}>
                  Pricing is <strong>indicative</strong>, based on the midpoint of your selected range
                  (<strong>{answers.guest_count_range?.label}</strong>). Enter an exact count in Step 1 for
                  a more precise estimate; a final quote will be confirmed after consultation.
                </p>
              )}
            </div>
          </div>
        )}

        {estimateLoading ? (
          <div style={{ background: PJ.white, border: `1.5px solid ${PJ.sand}`, borderRadius: 16, padding: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <Loader2 size={20} style={{ color: PJ.gold, animation: 'spin 1s linear infinite' }} />
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', color: PJ.slate }}>
              Calculating your estimate…
            </span>
          </div>
        ) : estimateError ? (
          <div style={{ background: '#FFF8F8', border: '1.5px solid #FFD5D5', borderRadius: 16, padding: '20px 24px' }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', color: '#CC3333', margin: 0 }}>
              {estimateError}
            </p>
            <button onClick={onRecalculate}
              style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6,
                fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem', color: PJ.gold,
                background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              <RefreshCw size={14} /> Try again
            </button>
          </div>
        ) : estimate ? (
          <div data-testid="ss-estimate-panel" style={{ background: PJ.white, border: `1.5px solid ${PJ.sand}`,
            borderRadius: 18, overflow: 'hidden' }}>
            {/* Service breakdown lines */}
            {(estimate.service_breakdowns || []).map((li, i) => (
              <div key={i} style={{ padding: '14px 20px', borderBottom: `1px solid ${PJ.sand}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', fontWeight: 500,
                    color: PJ.obsidian, margin: 0 }}>{li.service_name}</p>
                  {li.calculation_note && (
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: PJ.slate, margin: '2px 0 0' }}>
                      {li.calculation_note}
                    </p>
                  )}
                </div>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', fontWeight: 600,
                  color: PJ.obsidian, margin: 0, flexShrink: 0, marginLeft: 16 }}>
                  {li.line_total_display}
                </p>
              </div>
            ))}
            {/* Pricing-on-request or missing */}
            {estimate.pricing_missing?.length > 0 && (
              <div style={{ padding: '12px 20px', borderBottom: `1px solid ${PJ.sand}`,
                background: `${PJ.gold}06` }}>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.76rem', color: PJ.slate, margin: 0 }}>
                  {estimate.price_on_request_label || 'Price on Request'}: {estimate.pricing_missing.join(', ')} — confirmed during consultation
                </p>
              </div>
            )}
            {/* Total */}
            <div style={{ padding: '18px 20px', background: PJ.ivory, display: 'flex',
              justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.3rem', fontWeight: 600,
                  color: PJ.obsidian, margin: 0 }}>
                  {estimate.show_range ? 'Indicative Range' : 'Indicative Total'}
                </p>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', color: PJ.slate, margin: '2px 0 0' }}>
                  {estimate.show_tax_line ? 'Taxes included in line totals' : '+ taxes as applicable'} · Subject to final confirmation
                </p>
              </div>
              <p data-testid="ss-estimate-total" style={{ fontFamily: 'Cormorant Garamond, serif',
                fontSize: '1.8rem', fontWeight: 600, color: PJ.gold, margin: 0, flexShrink: 0, marginLeft: 20 }}>
                {estimate.show_range
                  ? `${estimate.range_minimum_display} – ${estimate.range_maximum_display}`
                  : (estimate.total_priced_display || estimate.subtotal_display)}
              </p>
            </div>
            {/* Disclaimer */}
            {estimate.disclaimer_text && (
              <div style={{ padding: '12px 20px', borderTop: `1px solid ${PJ.sand}`,
                background: PJ.ivory }}>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', color: PJ.slate, margin: 0, lineHeight: 1.5 }}>
                  {estimate.disclaimer_text}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div style={{ background: PJ.white, border: `1.5px dashed ${PJ.sand}`, borderRadius: 16,
            padding: '28px 24px', textAlign: 'center' }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', color: PJ.slate, margin: 0 }}>
              Complete your guest count and service selection to see a live estimate.
            </p>
          </div>
        )}
      </PhaseSection>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PHASE 5: CONNECT
// ══════════════════════════════════════════════════════════════════════════════

function PhaseConnect({ contact, onChange, onSubmit, submitState, leadRef, estimateRef, onRestart }) {
  if (submitState === 'SUCCESS') {
    return (
      <EnquiryConfirmationScreen
        publicReference={leadRef}
        onRestart={onRestart}
        testIdPrefix="ss"
      />
    );
  }

  return (
    <div>
      <PhaseSection title="Almost There">
        <QuestionLabel label="Let's stay in touch" subLabel="We'll send you a personalised consultation invite within 24 hours" />
        <div style={{ maxWidth: 480 }}>
          {[
            { key: 'name', label: 'Your Name', type: 'text', placeholder: 'Full name', testId: 'ss-contact-name', icon: User },
            { key: 'email', label: 'Email', type: 'email', placeholder: 'your@email.com', testId: 'ss-contact-email', icon: Mail },
            { key: 'phone', label: 'Phone', type: 'tel', placeholder: '+91 98xxx xxxxx', testId: 'ss-contact-phone', icon: Phone },
          ].map(f => {
            const Icon = f.icon;
            return (
              <div key={f.key} style={{ marginBottom: 16 }}>
                <label style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem', fontWeight: 600,
                  color: PJ.obsidian, display: 'block', marginBottom: 6 }}>{f.label}</label>
                <div style={{ position: 'relative' }}>
                  <Icon size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                    color: PJ.slate, pointerEvents: 'none' }} />
                  <input type={f.type} placeholder={f.placeholder}
                    data-testid={f.testId}
                    value={contact[f.key]}
                    onChange={e => onChange(f.key, e.target.value)}
                    style={{ width: '100%', padding: '11px 14px 11px 40px',
                      border: `1.5px solid ${PJ.sand}`, borderRadius: 12,
                      fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', color: PJ.obsidian,
                      background: PJ.white, outline: 'none', boxSizing: 'border-box',
                      transition: 'border-color 0.18s' }}
                    onFocus={e => (e.target.style.borderColor = PJ.gold)}
                    onBlur={e => (e.target.style.borderColor = PJ.sand)}
                  />
                </div>
              </div>
            );
          })}

          {/* Optional notes */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem', fontWeight: 600,
              color: PJ.obsidian, display: 'block', marginBottom: 6 }}>Anything you'd like to tell us? (optional)</label>
            <textarea
              data-testid="ss-contact-notes"
              placeholder="Any specific requirements, preferred meeting time, etc."
              value={contact.notes}
              onChange={e => onChange('notes', e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '11px 14px', border: `1.5px solid ${PJ.sand}`, borderRadius: 12,
                fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', color: PJ.obsidian,
                background: PJ.white, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
              onFocus={e => (e.target.style.borderColor = PJ.gold)}
              onBlur={e => (e.target.style.borderColor = PJ.sand)}
            />
          </div>

          {/* Callback toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
            background: PJ.sand, borderRadius: 12, padding: '12px 16px' }}>
            <button data-testid="ss-callback-toggle"
              aria-pressed={contact.requestCallback}
              onClick={() => onChange('requestCallback', !contact.requestCallback)}
              style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: contact.requestCallback ? PJ.gold : '#D0C8C0',
                position: 'relative', flexShrink: 0, transition: 'background 0.22s' }}>
              <div style={{ position: 'absolute', top: 3, left: contact.requestCallback ? 22 : 3,
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                transition: 'left 0.22s', boxShadow: '0 1px 4px rgba(0,0,0,0.18)' }} />
            </button>
            <div>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.86rem',
                color: PJ.obsidian, margin: 0 }}>Request a callback</p>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.76rem', color: PJ.slate, margin: '2px 0 0' }}>
                A dedicated planner will call you within 2 hours
              </p>
            </div>
          </div>

          {/* ── Gate 1: Explicit consent checkbox ───────────────────────── */}
          <div data-testid="ss-consent-section"
            style={{ background: contact.consentGiven ? `${PJ.forest}08` : `${PJ.blush}`,
              border: `1.5px solid ${contact.consentGiven ? `${PJ.forest}50` : `${PJ.gold}60`}`,
              borderRadius: 14, padding: '16px 18px', marginBottom: 16, transition: 'all 0.22s' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 14, cursor: 'pointer' }}>
              <div style={{ position: 'relative', flexShrink: 0, marginTop: 2 }}>
                <input
                  data-testid="ss-consent-checkbox"
                  type="checkbox"
                  checked={!!contact.consentGiven}
                  onChange={e => onChange('consentGiven', e.target.checked)}
                  style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                  aria-label="Contact consent agreement"
                />
                <div style={{ width: 20, height: 20, borderRadius: 6,
                  border: `2px solid ${contact.consentGiven ? PJ.forest : PJ.gold}`,
                  background: contact.consentGiven ? PJ.forest : PJ.white,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.18s' }}>
                  {contact.consentGiven && <Check size={11} style={{ color: '#fff' }} />}
                </div>
              </div>
              <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem',
                color: PJ.obsidian, lineHeight: 1.55 }}>
                {CONSENT_WORDING}
                <span style={{ display: 'block', marginTop: 4, fontFamily: 'DM Sans, sans-serif',
                  fontSize: '0.72rem', color: PJ.slate }}>
                  Policy version: {CONSENT_VERSION} · Required to submit your enquiry
                </span>
              </span>
            </label>
          </div>

          {/* ── Optional marketing consent ──────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <input
              data-testid="ss-marketing-consent-checkbox"
              type="checkbox"
              id="ss-mkt-consent"
              checked={!!contact.marketingConsent}
              onChange={e => onChange('marketingConsent', e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: PJ.gold }}
            />
            <label htmlFor="ss-mkt-consent" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem',
              color: PJ.slate, cursor: 'pointer', lineHeight: 1.45 }}>
              I would also like to receive occasional updates, inspiration and exclusive offers from Prashasta Events. (Optional)
            </label>
          </div>

          {submitState === 'ERROR' && (
            <div style={{ background: '#FFF8F8', border: '1.5px solid #FFD5D5', borderRadius: 10,
              padding: '12px 16px', marginBottom: 16 }}>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem', color: '#CC3333', margin: 0 }}>
                Something went wrong. Please try again.
              </p>
            </div>
          )}

          <button data-testid="ss-submit-lead-btn"
            onClick={onSubmit}
            disabled={submitState === 'LOADING' || !contact.name || !contact.phone || !contact.consentGiven}
            className="ss-btn-gold"
            style={{ width: '100%', padding: '14px 24px',
              background: (!contact.name || !contact.phone || !contact.consentGiven) ? PJ.sand : PJ.obsidian,
              color: (!contact.name || !contact.phone || !contact.consentGiven) ? PJ.slate : '#fff',
              border: 'none', borderRadius: 12, cursor: (!contact.name || !contact.phone || !contact.consentGiven) ? 'not-allowed' : 'pointer',
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              transition: 'background 0.22s' }}>
            {submitState === 'LOADING' ? (
              <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Submitting…</>
            ) : (
              <><ArrowRight size={18} /> Send My Plan to Prashasta</>
            )}
          </button>
        </div>
      </PhaseSection>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PLAN SUMMARY
// ══════════════════════════════════════════════════════════════════════════════

function PlanSummary({ answers, estimate, estimateLoading, phase, compact = false, onChangeEventType }) {
  const activeServices = answers.planning_scope === 'LUXURY'
    ? SERVICES.filter(s => ['VENUE','DECOR','CATERING','AV_PRODUCTION','GUEST_HOSPITALITY','PHOTOGRAPHY'].includes(s.value))
    : SERVICES.filter(s => (answers.selected_services || []).includes(s.value));

  const eventLabel = EVENT_TYPES.find(e => e.value === answers.event_type)?.label;
  const cityLabel = answers.location_city?.value === 'OTHER' ? answers.location_custom : CITIES.find(c => c.value === answers.location_city?.value)?.label;
  const guestLabel = answers.guest_count_exact ? `${parseInt(answers.guest_count_exact).toLocaleString()} guests` : answers.guest_count_range?.label;
  const hasAnyAnswer = !!(answers.event_type);

  if (!hasAnyAnswer) {
    return (
      <div style={{ background: PJ.white, border: `1.5px dashed ${PJ.sand}`, borderRadius: 18,
        padding: '28px 20px', textAlign: 'center' }}>
        <Sparkles size={24} style={{ color: PJ.gold, margin: '0 auto 12px', display: 'block' }} />
        <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.15rem', color: PJ.obsidian, margin: '0 0 8px' }}>
          Your plan will appear here
        </p>
        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem', color: PJ.slate, margin: 0, lineHeight: 1.5 }}>
          Start by selecting your event type
        </p>
      </div>
    );
  }

  return (
    <div data-testid="ss-plan-summary" style={{ background: PJ.white, border: `1.5px solid ${PJ.sand}`,
      borderRadius: 18, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ background: PJ.ivory, padding: '16px 20px', borderBottom: `1px solid ${PJ.sand}` }}>
        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.68rem', fontWeight: 600, color: PJ.gold,
          textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 4px' }}>Your Event Plan</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.25rem', fontWeight: 600,
            color: PJ.obsidian, margin: 0 }}>{eventLabel || 'Your Event'}</p>
          {onChangeEventType && (
            <button
              data-testid="ss-summary-change-event-type-btn"
              onClick={onChangeEventType}
              style={{
                background: 'none', border: `1px solid ${PJ.sand}`, borderRadius: 8,
                cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem',
                color: PJ.slate, padding: '3px 10px', flexShrink: 0,
                transition: 'background 0.18s',
              }}
            >
              Change
            </button>
          )}
        </div>
      </div>
      <div style={{ padding: '16px 20px' }}>
        {cityLabel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <MapPin size={13} style={{ color: PJ.gold, flexShrink: 0 }} />
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem', color: PJ.slate }}>{cityLabel}</span>
          </div>
        )}
        {answers.event_date && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ color: PJ.gold, flexShrink: 0, fontSize: '0.76rem' }}>📅</span>
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem', color: PJ.slate }}>
              {new Date(answers.event_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
            </span>
          </div>
        )}
        {guestLabel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Users size={13} style={{ color: PJ.gold, flexShrink: 0 }} />
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem', color: PJ.slate }}>{guestLabel}</span>
          </div>
        )}
        {answers.planning_scope && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Star size={13} style={{ color: PJ.gold, flexShrink: 0 }} />
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem', color: PJ.slate }}>
              {PLANNING_SCOPES.find(s => s.value === answers.planning_scope)?.label}
            </span>
          </div>
        )}
        {activeServices.length > 0 && !compact && (
          <div style={{ marginTop: 12, borderTop: `1px solid ${PJ.sand}`, paddingTop: 12 }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', fontWeight: 600,
              color: PJ.gold, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 8px' }}>
              Services ({activeServices.length})
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {activeServices.map(s => (
                <span key={s.value} style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.76rem',
                  background: PJ.sand, borderRadius: 20, padding: '3px 10px', color: PJ.obsidian }}>
                  {s.label}
                </span>
              ))}
            </div>
          </div>
        )}
        {/* Estimate teaser */}
        {(estimate || estimateLoading) && (
          <div style={{ marginTop: 14, borderTop: `1px solid ${PJ.sand}`, paddingTop: 14 }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', fontWeight: 600,
              color: PJ.gold, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 4px' }}>
              Indicative Estimate
            </p>
            {estimateLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Loader2 size={14} style={{ color: PJ.gold, animation: 'spin 1s linear infinite' }} />
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem', color: PJ.slate }}>Calculating…</span>
              </div>
            ) : (
              <p data-testid="ss-summary-estimate-total" style={{ fontFamily: 'Cormorant Garamond, serif',
                fontSize: '1.5rem', fontWeight: 600, color: PJ.gold, margin: 0 }}>
                {estimate?.show_range
                  ? `${estimate.range_minimum_display} – ${estimate.range_maximum_display}`
                  : (estimate?.total_priced_display || estimate?.subtotal_display)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STUDIO HEADER + PHASE PROGRESS
// ══════════════════════════════════════════════════════════════════════════════

function StudioHeader({ phase, onPhaseClick, completedPhases, user, loginPath }) {
  const currentIdx = PHASE_IDS.indexOf(phase);
  return (
    <header style={{ position: 'sticky', top: 0, zIndex: 40, background: PJ.ivory,
      borderBottom: `1px solid ${PJ.sand}`, padding: '0 24px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between', gap: 20, minHeight: 64 }}>
        {/* Wordmark */}
        <a href="/" style={{ textDecoration: 'none', flexShrink: 0 }}>
          <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', fontWeight: 600,
            color: PJ.obsidian, letterSpacing: '0.02em' }}>
            Prashasta
          </span>
        </a>
        {/* Phase stepper */}
        <nav data-testid="ss-phase-stepper"
          style={{ display: 'flex', alignItems: 'center', gap: 0, overflow: 'hidden', flex: 1, justifyContent: 'center' }}>
          {PHASES.map((p, i) => {
            const isActive = p.id === phase;
            const isDone = completedPhases.includes(p.id);
            const isClickable = isDone || i <= currentIdx;
            return (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center' }}>
                <button
                  data-testid={`ss-phase-${p.id.toLowerCase()}`}
                  onClick={() => isClickable && onPhaseClick(p.id)}
                  disabled={!isClickable}
                  title={p.hint}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    background: 'none', border: 'none', cursor: isClickable ? 'pointer' : 'default',
                    padding: '10px 12px', opacity: (!isActive && !isDone && i > currentIdx) ? 0.35 : 1 }}>
                  <div className="ss-progress-dot"
                    style={{ width: isActive ? 28 : 22, height: isActive ? 28 : 22, borderRadius: '50%',
                      background: isActive ? PJ.gold : isDone ? `${PJ.gold}30` : PJ.sand,
                      border: isActive ? `2px solid ${PJ.gold}` : isDone ? `1.5px solid ${PJ.gold}60` : `1.5px solid ${PJ.sand}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: isActive ? `0 0 0 4px ${PJ.gold}20` : 'none' }}>
                    {isDone && !isActive ? (
                      <Check size={10} style={{ color: PJ.gold }} />
                    ) : (
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.68rem', fontWeight: 700,
                        color: isActive ? '#fff' : PJ.slate }}>{i + 1}</span>
                    )}
                  </div>
                  <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.66rem', fontWeight: isActive ? 600 : 400,
                    color: isActive ? PJ.obsidian : PJ.slate, whiteSpace: 'nowrap',
                    display: 'none' /* hidden on mobile */, }}>
                    {p.label}
                  </span>
                </button>
                {i < PHASES.length - 1 && (
                  <div style={{ width: 24, height: 1,
                    background: completedPhases.includes(p.id) ? `${PJ.gold}50` : PJ.sand,
                    flexShrink: 0 }} />
                )}
              </div>
            );
          })}
        </nav>
        {/* Concierge button */}
        <button data-testid="ss-concierge-btn"
          style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: `1.5px solid ${PJ.sand}`, borderRadius: 24,
            padding: '7px 14px', cursor: 'pointer', transition: 'all 0.18s',
            fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', color: PJ.slate }}
          onMouseEnter={e => e.currentTarget.style.background = PJ.sand}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}>
          <MessageCircle size={14} style={{ color: PJ.gold }} />
          <span style={{ display: 'none' }}>Ask Prashasta</span>
        </button>

        {/* Staff Login / Open Workspace */}
        {user ? (
          <a
            href="/home"
            data-testid="ss-open-workspace-btn"
            title="Open Prashasta CRM"
            style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6,
              background: `linear-gradient(135deg, ${PJ.gold}, #B8924E)`,
              color: '#fff', border: 'none', borderRadius: 24,
              padding: '7px 16px', cursor: 'pointer', textDecoration: 'none',
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', fontWeight: 600,
              boxShadow: `0 2px 8px rgba(201,161,95,0.35)`, whiteSpace: 'nowrap' }}>
            <LayoutDashboard size={13} />
            <span style={{ display: 'none', '@media(min-width:480px)': { display: 'inline' } }}>Open Workspace</span>
          </a>
        ) : (
          <a
            href={loginPath}
            data-testid="ss-staff-login-btn"
            title="Open Workspace"
            style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6,
              background: 'none', border: `1.5px solid rgba(201,161,95,0.55)`,
              color: PJ.gold, borderRadius: 24,
              padding: '7px 16px', cursor: 'pointer', textDecoration: 'none',
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', fontWeight: 600,
              transition: 'all 0.18s', whiteSpace: 'nowrap' }}
            onMouseEnter={e => { e.currentTarget.style.background = PJ.gold; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = PJ.gold; }}>
            <LogIn size={13} />
            <span>Open Workspace</span>
          </a>
        )}
      </div>
    </header>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MOBILE SUMMARY BAR + DRAWER
// ══════════════════════════════════════════════════════════════════════════════

function MobileSummaryBar({ answers, estimate, estimateLoading, onOpen }) {
  const eventLabel = EVENT_TYPES.find(e => e.value === answers.event_type)?.label;
  return (
    <div className="ss-mobile-bar" data-testid="ss-mobile-summary-bar"
      style={{ display: 'none', position: 'fixed', bottom: 0, left: 0, right: 0,
        background: PJ.white, borderTop: `1px solid ${PJ.sand}`, zIndex: 30,
        padding: '12px 20px 20px', justifyContent: 'space-between', alignItems: 'center',
        boxShadow: '0 -4px 20px rgba(32,27,25,0.08)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.76rem', fontWeight: 600,
          color: PJ.gold, margin: 0, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {eventLabel ? `${eventLabel} Plan` : 'Your Plan'}
        </p>
        {estimateLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Loader2 size={12} style={{ color: PJ.gold, animation: 'spin 1s linear infinite' }} />
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem', color: PJ.slate }}>Calculating…</span>
          </div>
        ) : estimate ? (
          <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', fontWeight: 600,
            color: PJ.obsidian, margin: '2px 0 0' }}>
            {estimate?.show_range
              ? `${estimate.range_minimum_display} – ${estimate.range_maximum_display}`
              : (estimate?.total_priced_display || estimate?.subtotal_display)}
          </p>
        ) : (
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem', color: PJ.slate, margin: '2px 0 0' }}>
            {answers.event_type ? 'Configure services for estimate' : 'Start planning below'}
          </p>
        )}
      </div>
      <button onClick={onOpen} data-testid="ss-mobile-summary-open"
        style={{ display: 'flex', alignItems: 'center', gap: 6,
          background: PJ.sand, border: 'none', borderRadius: 20, padding: '8px 14px',
          cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem', color: PJ.obsidian,
          flexShrink: 0, marginLeft: 12 }}>
        <ChevronUp size={14} /> View plan
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PHASE NAVIGATION CONTROLS
// ══════════════════════════════════════════════════════════════════════════════

function PhaseNav({ phase, onBack, onContinue, canContinue, isLastPhase }) {
  const phaseIdx = PHASE_IDS.indexOf(phase);
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      paddingTop: 28, marginTop: 28, borderTop: `1px solid ${PJ.sand}` }}>
      {phaseIdx > 0 ? (
        <button data-testid="ss-phase-back-btn" onClick={onBack}
          className="ss-btn-outline"
          style={{ display: 'flex', alignItems: 'center', gap: 8,
            background: 'transparent', border: `1.5px solid ${PJ.sand}`, borderRadius: 12,
            padding: '11px 20px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
            fontSize: '0.9rem', color: PJ.slate, transition: 'all 0.18s' }}>
          ← Back
        </button>
      ) : <div />}
      <button data-testid="ss-phase-continue-btn" onClick={onContinue}
        disabled={!canContinue}
        className="ss-btn-gold"
        style={{ display: 'flex', alignItems: 'center', gap: 8,
          background: canContinue ? PJ.obsidian : PJ.sand,
          border: 'none', borderRadius: 12, padding: '11px 24px',
          cursor: canContinue ? 'pointer' : 'not-allowed',
          fontFamily: 'DM Sans, sans-serif', fontSize: '0.92rem', fontWeight: 600,
          color: canContinue ? '#fff' : PJ.slate, transition: 'all 0.22s' }}>
        {isLastPhase ? 'Submit' : 'Continue'} <ChevronRight size={16} />
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN SMART STUDIO COMPONENT
// ══════════════════════════════════════════════════════════════════════════════

export default function SmartStudio() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, uatConfig } = useAuth();
  const loginPath = uatConfig?.enabled ? "/uat-login" : "/login";
  const incomingCategoryId = searchParams.get('category_id');
  const incomingServiceCode = incomingCategoryId ? (CATEGORY_SERVICE_MAP[incomingCategoryId] || null) : null;
  // UX-PUBLIC-01: Pre-populate event type when arriving from Discover screen
  const incomingEventType = searchParams.get('event_type');
  const incomingEventTypeValid = !!(incomingEventType && EVENT_TYPES.some(e => e.value === incomingEventType));

  // ── Session recovery: restore from sessionStorage if within TTL ───────────
  const getInitialState = () => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (Date.now() - saved.ts < SESSION_TTL_MS && saved.answers?.event_type) {
          const { session_id, event_type, location_city, location_custom, event_date,
            guest_count_range, guest_count_exact, wedding_functions, conference_type,
            style_preference, budget_range, planning_scope, selected_services,
            venue_status, preferred_partners, preselected_category_id } = saved.answers || {};
          const safeAnswers = {
            session_id, event_type, location_city, location_custom, event_date,
            guest_count_range, guest_count_exact, wedding_functions, conference_type,
            style_preference, budget_range, planning_scope, selected_services,
            venue_status,
            preferred_partners: preferred_partners || {},
            preselected_category_id: preselected_category_id || null,
          };
          return { answers: safeAnswers, phase: saved.phase || 'DISCOVER',
            completed: saved.completed || [] };
        }
      }
    } catch { /* ignore */ }
    return { answers: null, phase: null, completed: [] };
  };
  const [sessionRecovery] = useState(() => getInitialState());

  // ── State ──────────────────────────────────────────────────────────────────
  const [phase, setPhase]           = useState(sessionRecovery.phase || 'DISCOVER');
  const [answers, setAnswers]       = useState(() => {
    const base = sessionRecovery.answers || createEmptyStudioState();
    // UX-PUBLIC-01: Pre-populate event type from Discover screen URL param (only if no saved session)
    if (incomingEventTypeValid && !base.event_type) {
      return { ...base, event_type: incomingEventType, preferred_partners: base.preferred_partners || {} };
    }
    // UX-HOME-01: If arriving from homepage category click, pre-select service
    if (incomingServiceCode && !base.event_type) {
      return {
        ...base,
        planning_scope: 'BUILD_OWN',
        selected_services: [incomingServiceCode],
        preselected_category_id: incomingCategoryId,
        preferred_partners: base.preferred_partners || {},
      };
    }
    return { ...base, preferred_partners: base.preferred_partners || {} };
  });
  const [completedPhases, setCP]    = useState(sessionRecovery.completed);
  // UX-PUBLIC-01: True when event type was pre-set from EventJourney Discover screen.
  // Suppresses the event-type card grid; shows compact confirmation banner instead.
  // Resets to false when customer explicitly clicks "Change".
  const [eventTypeFromUrl, setEventTypeFromUrl] = useState(
    () => incomingEventTypeValid && !sessionRecovery.answers?.event_type
  );
  const [contact, setContact]       = useState({ name:'', email:'', phone:'', notes:'',
    requestCallback: false, consentGiven: false, marketingConsent: false });
  const [hasResumeBanner, setResume] = useState(!!sessionRecovery.answers?.event_type);
  const [estimate, setEstimate]     = useState(null);
  const [estimateLoading, setEL]    = useState(false);
  const [estimateError, setEE]      = useState(null);
  const [estimateRef, setER]        = useState(null);
  const [serviceMap, setServiceMap] = useState(null);
  const [submitState, setSS]        = useState('IDLE');
  const [leadRef, setLeadRef]       = useState(null);
  const [drawerOpen, setDrawer]     = useState(false);

  const debounceRef = useRef(null);
  const idemRef     = useRef((crypto.randomUUID?.() ?? `ss-${Date.now()}`));
  const staleRef    = useRef(0);
  const triggerPricingRef = useRef(null);

  // ── UX-HOME-01: Partner preference toggle ─────────────────────────────────
  const handleTogglePartner = useCallback((serviceCode, partner) => {
    setAnswers(prev => {
      const current = prev.preferred_partners || {};
      const existing = current[serviceCode];
      if (existing?.supplier_id === partner.id) {
        // deselect
        const next = { ...current };
        delete next[serviceCode];
        return { ...prev, preferred_partners: next };
      }
      return {
        ...prev,
        preferred_partners: {
          ...current,
          [serviceCode]: {
            supplier_id: partner.id,
            partner_name: partner.name,
            category_id: partner.category_id,
          },
        },
      };
    });
  }, []);

  // ── G5: Persist ONLY non-sensitive planning answers and navigation state ─────
  useEffect(() => {
    if (!answers.event_type) return; // don't save empty sessions
    try {
      const { session_id, event_type, location_city, location_custom, event_date,
        guest_count_range, guest_count_exact, wedding_functions, conference_type,
        style_preference, budget_range, planning_scope, selected_services,
        venue_status, preferred_partners, preselected_category_id } = answers;
      const safePlanningData = {
        session_id, event_type, location_city, location_custom, event_date,
        guest_count_range, guest_count_exact, wedding_functions, conference_type,
        style_preference, budget_range, planning_scope, selected_services,
        venue_status,
        preferred_partners: preferred_partners || {},
        preselected_category_id: preselected_category_id || null,
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        answers: safePlanningData,
        phase, completed: completedPhases, ts: Date.now(),
      }));
    } catch { /* quota exceeded — ignore */ }
  }, [answers, phase, completedPhases]);

  // ── G5: Clear session (answers + contact) on successful submission ──────────
  useEffect(() => {
    if (submitState === 'SUCCESS') {
      try {
        sessionStorage.removeItem(SESSION_KEY);
        // Also clear any legacy contact data that may have been cached
        sessionStorage.removeItem('prashasta-studio-contact-v1');
      } catch { /* ignore */ }
    }
  }, [submitState]);

  // ── Load service map once ─────────────────────────────────────────────────
  useEffect(() => {
    publicEstimateApi.getServiceMap(TENANT_ALIAS)
      .then(r => setServiceMap(r.data?.service_map || r.data || null))
      .catch(() => setServiceMap(null));
  }, []);

  // ── Answer update ─────────────────────────────────────────────────────────
  const handleAnswerChange = useCallback((key, value) => {
    setAnswers(prev => {
      const next = { ...prev, [key]: value };
      // Clear dependent answers
      const invalidated = getInvalidatedIds(key, next);
      invalidated.forEach(id => { next[id] = Array.isArray(prev[id]) ? [] : null; });
      return next;
    });
    // Trigger pricing recalculation for relevant fields
    const pricingKeys = ['guest_count_exact', 'selected_services', 'event_date', 'location_city', 'planning_scope', 'guest_count_range'];
    if (pricingKeys.includes(key)) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => triggerPricingRef.current?.(), 600);
    }
  }, []);

  // UX-PUBLIC-01: Allow customer to re-select event type from confirmed banner or plan summary
  const handleChangeEventType = useCallback(() => {
    setEventTypeFromUrl(false);
    setPhase('DISCOVER');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ── GQP pricing ───────────────────────────────────────────────────────────
  const triggerPricing = useCallback(() => {
    setAnswers(current => {
      const guestCount = resolveGuestCount(current);
      const activeServices = current.planning_scope === 'LUXURY'
        ? ['VENUE','DECOR','CATERING','AV_PRODUCTION','GUEST_HOSPITALITY','PHOTOGRAPHY']
        : (current.selected_services || []);
      if (!guestCount || !activeServices.length) return current;

      const token = ++staleRef.current;
      setEL(true); setEE(null);
      const req = buildEstimateRequest(current, serviceMap, idemRef.current);

      publicEstimateApi.calculate(req)
        .then(r => {
          if (staleRef.current !== token) return; // stale response
          setEstimate(r.data);
          setEE(null); // clear any prior error
          setER(r.data?.public_reference || r.data?.estimate_ref || null);
          setEL(false);
        })
        .catch(() => {
          if (staleRef.current !== token) return;
          setEE("Unable to calculate estimate. Your plan is saved — we'll calculate it during consultation.");
          setEL(false);
        });
      return current;
    });
  }, [serviceMap]);

  // Keep ref current so handleAnswerChange always calls the latest version
  useEffect(() => { triggerPricingRef.current = triggerPricing; }, [triggerPricing]);

  // Recalculate when switching to REVIEW
  useEffect(() => {
    if (phase === 'REVIEW') triggerPricing();
  }, [phase, triggerPricing]);

  // ── Phase navigation ──────────────────────────────────────────────────────
  const goToPhase = useCallback((nextPhase) => {
    const curIdx = PHASE_IDS.indexOf(phase);
    const nxtIdx = PHASE_IDS.indexOf(nextPhase);
    if (nxtIdx > curIdx) {
      setCP(prev => [...new Set([...prev, phase])]);
    }
    setPhase(nextPhase);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [phase]);

  const handleContinue = useCallback(() => {
    const idx = PHASE_IDS.indexOf(phase);
    if (idx < PHASE_IDS.length - 1) {
      goToPhase(PHASE_IDS[idx + 1]);
    } else {
      handleSubmit();
    }
  }, [phase, goToPhase]);

  const handleBack = useCallback(() => {
    const idx = PHASE_IDS.indexOf(phase);
    if (idx > 0) setPhase(PHASE_IDS[idx - 1]);
  }, [phase]);

  const canContinue = isPhaseComplete(phase, answers) ||
    phase === 'REVIEW' || phase === 'PERSONALIZE';

  // ── CRM lead submit ───────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!contact.name || !contact.phone || !contact.consentGiven) return;
    setSS('LOADING');
    try {
      // Resolve city: if custom city entered use that, otherwise use the selected value
      const eventCity = answers.location_city?.value === 'OTHER'
        ? (answers.location_custom || undefined)
        : (answers.location_city?.value || undefined);

      const payload = {
        journey_session_id: answers.session_id,
        source_channel: 'WEBSITE_STUDIO',
        contact_name: contact.name,
        contact_mobile: contact.phone,
        ...(contact.email ? { contact_email: contact.email } : {}),
        ...(answers.event_type ? { event_type: answers.event_type } : {}),
        ...(answers.event_date ? { event_date: answers.event_date } : {}),
        ...(eventCity ? { event_city: eventCity } : {}),
        ...(answers.guest_count_range?.value ? { guest_count_range: answers.guest_count_range.value } : {}),
        ...(answers.guest_count_exact ? { guest_count_exact: parseInt(answers.guest_count_exact) } : {}),
        ...(answers.venue_status ? { venue_status: answers.venue_status } : {}),
        ...(answers.budget_range ? { budget_range: answers.budget_range } : {}),
        planning_scope: answers.planning_scope === 'LUXURY' ? 'LUXURY_CONCIERGE' : 'CUSTOM_EVENT',
        planning_mode: answers.planning_scope === 'LUXURY' ? 'LUXURY_CONCIERGE' : 'CUSTOM_EVENT',
        service_interests: answers.planning_scope === 'LUXURY'
          ? ['VENUE','DECOR','CATERING','AV_PRODUCTION','GUEST_HOSPITALITY','PHOTOGRAPHY']
          : (answers.selected_services?.length ? answers.selected_services : undefined),
        ...(answers.style_preference ? { style_preferences: [answers.style_preference] } : {}),
        ...(answers.wedding_functions?.length ? { event_functions: answers.wedding_functions } : {}),
        ...(estimateRef ? { estimate_ref: estimateRef } : {}),
        // UX-HOME-01: Partner preferences
        ...(answers.preferred_partners && Object.keys(answers.preferred_partners).length > 0 ? {
          preferred_partners: Object.entries(answers.preferred_partners).map(([svcCode, p]) => ({
            category_id: p.category_id || SERVICE_CATEGORY_MAP[svcCode] || svcCode,
            supplier_id: p.supplier_id,
            partner_name: p.partner_name || '',
            service_code: svcCode,
          })),
        } : {}),
        consent_given: contact.consentGiven,
        consent_wording: CONSENT_WORDING,
        consent_version: CONSENT_VERSION,
        consent_channel_scope: ['PHONE', 'WHATSAPP', 'EMAIL'],
        consent_timestamp: new Date().toISOString(),
        marketing_consent: contact.marketingConsent || false,
        callback_requested: contact.requestCallback || false,
        ...(contact.notes ? { callback_notes: contact.notes } : {}),
      };
      const r = await publicLeadApi.createProvisionalLead(payload);
      setLeadRef(r.data?.lead_id || r.data?.public_reference || r.data?.lead_number || null);
      setSS('SUCCESS');
    } catch {
      setSS('ERROR');
    }
  }, [contact, answers, estimateRef]);

  // ── Phase title ───────────────────────────────────────────────────────────
  const currentPhaseObj = PHASES.find(p => p.id === phase);

  // ── Restart: clear all session state and return to the first phase ─────────
  const handleRestartStudio = useCallback(() => {
    setAnswers(createEmptyStudioState());
    setPhase('DISCOVER');
    setCP([]);
    setSS('IDLE');
    setLeadRef(null);
    setER(null);
    setEventTypeFromUrl(false);
    setContact({ name: '', email: '', phone: '', notes: '', consentGiven: false, marketingConsent: false, requestCallback: false });
    try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  }, []);

  return (
    <>
      <style>{SS_STYLES}</style>
      <div style={{ minHeight: '100vh', background: PJ.ivory, fontFamily: 'DM Sans, sans-serif' }}>
        <StudioHeader phase={phase} onPhaseClick={goToPhase} completedPhases={completedPhases} user={user} loginPath={loginPath} />

        {/* Phase intro banner */}
        <div style={{ background: PJ.white, borderBottom: `1px solid ${PJ.sand}`, padding: '16px 24px' }}>
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.9rem', fontWeight: 600,
              color: PJ.obsidian, margin: 0, lineHeight: 1.2 }}>
              {currentPhaseObj?.label}
            </h1>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', color: PJ.slate, margin: '4px 0 0' }}>
              {currentPhaseObj?.hint}
            </p>
          </div>
        </div>

        {/* Session resume banner */}
        {hasResumeBanner && (
          <div data-testid="ss-session-resume-banner"
            style={{ background: `${PJ.gold}10`, borderBottom: `1px solid ${PJ.gold}30`,
              padding: '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem', color: PJ.obsidian, margin: 0 }}>
              We found a saved plan from an earlier session.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setResume(false)}
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', fontWeight: 600,
                  color: PJ.gold, background: 'none', border: `1px solid ${PJ.gold}`, borderRadius: 8,
                  padding: '4px 12px', cursor: 'pointer' }}>
                Continue saved plan
              </button>
              <button data-testid="ss-session-discard-btn"
                onClick={() => {
                  setAnswers(createEmptyStudioState());
                  setPhase('DISCOVER');
                  setCP([]);
                  setResume(false);
                  setEventTypeFromUrl(false);
                  try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
                }}
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem',
                  color: PJ.slate, background: 'none', border: `1px solid ${PJ.sand}`, borderRadius: 8,
                  padding: '4px 12px', cursor: 'pointer' }}>
                Start fresh
              </button>
            </div>
          </div>
        )}

        {/* Main layout */}
        <main style={{ maxWidth: 1180, margin: '0 auto', padding: '32px 24px 120px' }}>
          <div className="ss-layout" style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 32 }}>
            {/* Question area */}
            <div data-testid={`ss-phase-${phase.toLowerCase()}-content`}>
              {phase === 'DISCOVER' && (
                <PhaseDiscover
                  answers={answers}
                  onChange={handleAnswerChange}
                  eventTypeFromUrl={eventTypeFromUrl}
                  onChangeEventType={handleChangeEventType}
                />
              )}
              {phase === 'PERSONALIZE' && (
                <PhasePersonalise answers={answers} onChange={handleAnswerChange} />
              )}
              {phase === 'CONFIGURE' && (
                <PhaseConfigure answers={answers} onChange={handleAnswerChange}
                  preferredPartners={answers.preferred_partners || {}}
                  onTogglePartner={handleTogglePartner} />
              )}
              {phase === 'REVIEW' && (
                <PhaseReview answers={answers} estimate={estimate}
                  estimateLoading={estimateLoading} estimateError={estimateError}
                  onRecalculate={triggerPricing} />
              )}
              {phase === 'CONNECT' && (
                <PhaseConnect contact={contact}
                  onChange={(k, v) => setContact(prev => ({ ...prev, [k]: v }))}
                  onSubmit={handleSubmit} submitState={submitState}
                  leadRef={leadRef} estimateRef={estimateRef}
                  onRestart={handleRestartStudio} />
              )}
              {phase !== 'CONNECT' && (
                <PhaseNav phase={phase} onBack={handleBack} onContinue={handleContinue}
                  canContinue={canContinue} isLastPhase={phase === 'CONNECT'} />
              )}
            </div>

            {/* Sticky sidebar */}
            <div className="ss-sidebar ss-sticky-summary">
              <PlanSummary answers={answers} estimate={estimate}
                estimateLoading={estimateLoading} phase={phase}
                onChangeEventType={handleChangeEventType} />
            </div>
          </div>
        </main>

        {/* Mobile summary bar */}
        <MobileSummaryBar answers={answers} estimate={estimate}
          estimateLoading={estimateLoading} onOpen={() => setDrawer(true)} />

        {/* Mobile drawer */}
        {drawerOpen && (
          <>
            <div className="ss-drawer-overlay" onClick={() => setDrawer(false)} />
            <div className="ss-drawer">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', fontWeight: 600,
                  color: PJ.obsidian, margin: 0 }}>Your Plan</p>
                <button onClick={() => setDrawer(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                  <X size={20} style={{ color: PJ.slate }} />
                </button>
              </div>
              <PlanSummary answers={answers} estimate={estimate}
                estimateLoading={estimateLoading} phase={phase}
                onChangeEventType={handleChangeEventType} compact />
            </div>
          </>
        )}
      </div>
    </>
  );
}
